// CategoricalAssignment — the VAT Tier-3 category re-banding island.
//
// A compact assignment grid: one row per goods category, each a segmented
// control of the surviving VAT bands (21% / 12% / 6%). Moving a category to a
// different band is the edit. Rows are chunked under their section heading so
// the ~30-row grid stays scannable; a row sitting at its baseline band stays
// visually quiet. This is "chart-as-control" where the chart is the grid, not
// a continuous plot — see notes/tier3-vat-categorical-design.md.
//
// State is the commit itself: a record { categoryId -> chosen bandId } holding
// only the categories the user moved off their default. That record is written
// to Shiny via setInputValue(inputId, …); the server reads it at
// input[[group$id]] and the engine transform (.expand_categorical_commit) emits
// each category's targets at the chosen band's rate. A category at its default
// band is absent from the record (identity → no modification).
//
// Accessibility: each row's band choices are a radiogroup (role="radio" +
// aria-checked); the row label names the group. Bands always show the % number
// so the control never relies on colour alone.

import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  CategoricalAssignmentProps,
  CategoricalBand,
  CategoricalCategory,
} from "./types";
import { EXEMPT_BAND } from "./types";
import { useResetAllSignal } from "../../state/reset_signal";
import { recordControl } from "../../state/share_store";
import type { ControlMeta } from "../../state/share_store";
import { notifyRestored, restoredCategorical } from "../../state/share_restore";

type Commit = Record<string, string>;

function bandLabel(band: CategoricalBand): string {
  return `${Math.round(band.rate * 100)}%`;
}

// Stable, first-appearance order of the section headings.
function orderedSections(categories: CategoricalCategory[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of categories) {
    if (!seen.has(c.section)) {
      seen.add(c.section);
      out.push(c.section);
    }
  }
  return out;
}

export function CategoricalAssignment({
  inputId,
  label,
  instrument,
  description,
  bands,
  categories,
}: CategoricalAssignmentProps): JSX.Element {
  // The commit is the state: only categories moved off their default appear.
  // A shared `?s=` link seeds it (keeping only still-valid, non-default
  // assignments); otherwise it starts empty. (#244)
  const [commit, setCommit] = useState<Commit>(() =>
    restoredCategorical(
      inputId,
      bands.map((b) => b.id),
      Object.fromEntries(categories.map((c) => [c.id, c.defaultBand])),
    ),
  );

  const reset = useCallback(() => setCommit({}), []);
  // Page-level "Reset all" reaches React islands via the document-delegated
  // click signal (this grid isn't a Shiny widget). #132.
  useResetAllSignal(reset);

  // Static display metadata for the "Your reform" modal (#65); the modal lists
  // one row per reassigned category, resolving the chosen and baseline band
  // rates from these arrays.
  const meta = useMemo<ControlMeta>(
    () => ({
      kind: "categorical",
      label,
      instrument,
      categories: categories.map((c) => ({
        id: c.id,
        label: c.label,
        defaultBand: c.defaultBand,
      })),
      bands: bands.map((b) => ({ id: b.id, rate: b.rate })),
    }),
    [label, instrument, categories, bands],
  );

  // Push the commit to Shiny on every change. The server reads the latest when
  // the user clicks Simulate.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.Shiny?.setInputValue?.(inputId, commit, { priority: "deferred" });
    // Mirror into the share registry for the Copy-link button (#244) and the
    // reform modal (#65).
    recordControl(inputId, commit, Object.keys(commit).length > 0, meta);
    // Report a restored control so the shared reform auto-runs once every
    // control has committed (#297). No-op unless this id was restored.
    notifyRestored(inputId);
  }, [inputId, commit, meta]);

  const defaultBandOf = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of categories) m[c.id] = c.defaultBand;
    return m;
  }, [categories]);

  // Set a category to a band. Choosing the default band clears the entry
  // (back to identity); any other band records the move.
  const choose = useCallback(
    (categoryId: string, bandId: string) => {
      setCommit((prev) => {
        const next = { ...prev };
        if (bandId === defaultBandOf[categoryId]) {
          delete next[categoryId];
        } else {
          next[categoryId] = bandId;
        }
        return next;
      });
    },
    [defaultBandOf],
  );

  const resetRow = useCallback((categoryId: string) => {
    setCommit((prev) => {
      if (!(categoryId in prev)) return prev;
      const next = { ...prev };
      delete next[categoryId];
      return next;
    });
  }, []);

  const sections = useMemo(() => orderedSections(categories), [categories]);

  return (
    <div
      className="beamm-categorical-assignment"
      role="group"
      aria-label={label}
      data-testid={`categorical-assignment-${inputId}`}
    >
      {description ? (
        <p className="beamm-categorical-assignment__intro">{description}</p>
      ) : null}

      {sections.map((section) => {
        const inSection = categories.filter((c) => c.section === section);
        const modifiedCount = inSection.filter((c) => c.id in commit).length;
        return (
          <details
            key={section}
            className="beamm-categorical-assignment__section"
            data-testid={`categorical-assignment-section-${section}`}
          >
            <summary className="beamm-categorical-assignment__section-summary">
              <span className="beamm-categorical-assignment__section-title">
                {section}
              </span>
              <span className="beamm-categorical-assignment__section-count">
                {inSection.length}
              </span>
              {modifiedCount > 0 ? (
                <span
                  className="beamm-categorical-assignment__section-badge"
                  data-testid={`categorical-assignment-section-badge-${section}`}
                >
                  {modifiedCount} changed
                </span>
              ) : null}
            </summary>
            <div className="beamm-categorical-assignment__rows">
              {inSection.map((category) => {
                const current = commit[category.id] ?? category.defaultBand;
                const isModified = category.id in commit;
                const isExemptBaseline =
                  category.defaultBand === EXEMPT_BAND && !isModified;
                return (
                  <div
                    key={category.id}
                    className={
                      "beamm-categorical-assignment__row" +
                      (isModified
                        ? " beamm-categorical-assignment__row--modified"
                        : "")
                    }
                    data-testid={`categorical-assignment-row-${category.id}`}
                  >
                    <span className="beamm-categorical-assignment__category">
                      {isModified ? (
                        <span
                          className="beamm-categorical-assignment__modified-dot"
                          title="Modified from baseline"
                          aria-label="Modified from baseline"
                          data-testid={`categorical-assignment-modified-${category.id}`}
                        />
                      ) : null}
                      {category.label}
                    </span>

                    <span
                      className="beamm-categorical-assignment__bands"
                      role="radiogroup"
                      aria-label={`VAT band for ${category.label}`}
                    >
                      {bands.map((band) => {
                        const checked = current === band.id;
                        return (
                          <button
                            key={band.id}
                            type="button"
                            role="radio"
                            aria-checked={checked}
                            className={
                              "beamm-categorical-assignment__band" +
                              (checked
                                ? " beamm-categorical-assignment__band--active"
                                : "")
                            }
                            data-testid={`categorical-assignment-band-${category.id}-${band.id}`}
                            onClick={() => choose(category.id, band.id)}
                          >
                            {bandLabel(band)}
                          </button>
                        );
                      })}
                    </span>

                    <span className="beamm-categorical-assignment__trailing">
                      {isExemptBaseline ? (
                        <span className="beamm-categorical-assignment__exempt">
                          Exempt (0%)
                        </span>
                      ) : null}
                      {isModified ? (
                        <button
                          type="button"
                          className="beamm-categorical-assignment__reset beamm-input-island__reset"
                          data-testid={`categorical-assignment-reset-${category.id}`}
                          onClick={() => resetRow(category.id)}
                        >
                          Reset
                        </button>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          </details>
        );
      })}
    </div>
  );
}
